import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Clock, DollarSign, Calendar, Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { useServicesRealtime } from '@/hooks/useServicesRealtime';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

type Service = {
  id: string;
  name: string;
  description: string | null;
  price: number | string;
  duration_minutes: number;
  pre_service_instructions?: string | null;
};

export default function ServicesSection() {
  useServicesRealtime(['services']);
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Service | null>(null);

  const { data: services } = useQuery({
    queryKey: ['services'],
    queryFn: async () => {
      const { data, error } = await supabase.from('services').select('*').eq('active', true).order('name');
      if (error) throw error;
      return data as Service[];
    },
  });

  return (
    <section id="services" className="py-16 sm:py-20 bg-background">
      <div className="container mx-auto px-3 sm:px-4">
        <div className="text-center mb-10 sm:mb-14">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-3">
            Nossos Serviços
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto px-2">
            Toque em um serviço para ver os detalhes
          </p>
        </div>

        <div className="grid grid-cols-1 min-[480px]:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 max-w-6xl mx-auto">
          {services?.map((service, i) => (
            <motion.button
              key={service.id}
              type="button"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              viewport={{ once: true }}
              onClick={() => setSelected(service)}
              aria-label={`Ver detalhes de ${service.name}`}
              className="glass-card rounded-xl p-4 sm:p-5 min-h-[44px] min-w-0 text-left hover:shadow-md hover:border-primary/40 transition-all group focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <div className="flex items-start justify-between gap-2 mb-3 min-w-0">
                <h3 className="font-display text-sm sm:text-base font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 min-w-0">
                  {service.name}
                </h3>
                <Info className="h-4 w-4 text-muted-foreground/60 shrink-0 mt-0.5 group-hover:text-primary transition-colors" />
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2 text-xs sm:text-sm">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {service.duration_minutes}min
                </span>
                <span className="flex items-center gap-1 text-primary font-semibold">
                  <DollarSign className="h-4 w-4" />R$ {Number(service.price).toFixed(2)}
                </span>
              </div>
            </motion.button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-10 sm:mt-12 flex justify-center"
        >
          <Button
            variant="hero"
            size="xl"
            onClick={() => navigate('/booking')}
            aria-label="Agendar horário"
          >
            <Calendar className="h-5 w-5" />
            Agendar Horário
          </Button>
        </motion.div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-hidden gap-4 p-4 sm:p-6 [&]:flex [&]:flex-col">
          {selected && (
            <>
              <DialogHeader className="shrink-0 text-left">
                <DialogTitle className="font-display text-lg sm:text-xl pr-6">
                  {selected.name}
                </DialogTitle>
                <DialogDescription className="sr-only">Detalhes do serviço</DialogDescription>
              </DialogHeader>

              <div className="shrink-0 flex items-center justify-between flex-wrap gap-2 text-sm">
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {selected.duration_minutes} minutos
                </span>
                <span className="flex items-center gap-1 text-primary font-semibold text-base">
                  <DollarSign className="h-4 w-4" />R$ {Number(selected.price).toFixed(2)}
                </span>
              </div>

              <ScrollArea className="h-[min(50vh,15rem)] sm:h-[18rem] max-[820px]:landscape:h-[40vh] -mx-1 pr-3">
                <div className="space-y-4 px-1 pb-2">
                  {selected.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">Sobre o serviço</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                        {selected.description}
                      </p>
                    </div>
                  )}

                  {selected.pre_service_instructions && (
                    <div className="rounded-lg bg-primary/5 border border-primary/15 p-3">
                      <h4 className="text-sm font-semibold text-foreground mb-1">Antes do atendimento</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                        {selected.pre_service_instructions}
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>

              <Button
                variant="hero"
                className="shrink-0 w-full min-h-[44px]"
                onClick={() => {
                  setSelected(null);
                  navigate('/booking');
                }}
              >
                <Calendar className="h-4 w-4" />
                Agendar este serviço
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
